import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./learningStyle.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { getLsQuestions } from "../../functions/getLsQuestions";
import { useDocumentTitle } from "../../functions/useDocumentTitle";

const MAX_RULES = 6;
const MAX_RULE_LEN = 200;
const MAX_THANKYOU_LEN = 300;
const MAX_TAB_NAME_LEN = 30;
const OPTION_BOX_COLOR_KEYS = ["option_box_color1", "option_box_color2", "option_box_color3", "option_box_color4", "option_box_color5", "option_box_color6"];
const DEFAULT_OPTION_BOX_COLORS = ["#f8bfd2", "#bfe8bc", "#f0d9a3", "#bcc9f2", "#ddc2f2", "#bdeee3"];

const ensureArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

const clampInt = (n, min, max, fallback) => {
  const x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return fallback;
  return Math.min(max, Math.max(min, x));
};

const MIN_NO_OF_QUESTION = 1;
const FALLBACK_MAX_NO_OF_QUESTION = 100;

/** Strips anything but digits as the user types — blocks "-", "+", "e", letters, decimals. */
const digitsOnly = (raw) => String(raw ?? "").replace(/\D/g, "");

function Rules() {
  useDocumentTitle("Rules Settings - Learning Style Admin");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [rules, setRules] = useState([""]);
  const [tabName, setTabName] = useState("");
  const [customThankYouText, setCustomThankYouText] = useState("");
  const [noOfQuestion, setNoOfQuestion] = useState("12");
  const [shuffle, setShuffle] = useState(true);
  const [textColor, setTextColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState("#e07b39");
  const [buttonTextColor, setButtonTextColor] = useState("#ffffff");
  const [questionTextColor, setQuestionTextColor] = useState("#2d2d2d");
  const [optionBoxColors, setOptionBoxColors] = useState(DEFAULT_OPTION_BOX_COLORS);
  const [answerColor, setAnswerColor] = useState("#e2e8f0");
  const [totalQuestions, setTotalQuestions] = useState(null);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!adminToken) return;
    (async () => {
      try {
        const res = await getLsQuestions({ token: adminToken });
        setTotalQuestions((res.questions || []).length);
      } catch {
        setTotalQuestions(null);
      }
    })();
  }, [adminToken]);

  useEffect(() => {
    if (!data) return;

    setRules(ensureArray(data.rules).length ? ensureArray(data.rules).map(String) : [""]);
    setTabName(data.tab_name || "");
    setCustomThankYouText(data.custom_text_thank_you_page || "");
    setNoOfQuestion(String(data.no_of_question ?? 12));
    setShuffle(data.shuffle == null ? true : !!data.shuffle);
    if (data.text_color) setTextColor(data.text_color);
    if (data.button_color) setButtonColor(data.button_color);
    if (data.button_text_color) setButtonTextColor(data.button_text_color);
    if (data.question_text_color) setQuestionTextColor(data.question_text_color);
    setOptionBoxColors(OPTION_BOX_COLOR_KEYS.map((key, i) => data[key] || DEFAULT_OPTION_BOX_COLORS[i]));
    if (data.answer_color) setAnswerColor(data.answer_color);

    setLoading(false);
  }, [data]);

  const handleRuleChange = (idx, val) => {
    if (val.length > MAX_RULE_LEN) {
      Swal.fire("Limit", `Each rule can be at most ${MAX_RULE_LEN} characters.`, "warning");
      val = val.slice(0, MAX_RULE_LEN);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Limit", `Up to ${MAX_RULES} rules.`, "warning");
      return;
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      Swal.fire("Required", "At least one rule is required.", "warning");
      return;
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const trimmedRules = rules.map((r) => r.trim()).filter(Boolean);
    if (trimmedRules.length === 0) return { ok: false, msg: "At least one rule is required." };
    for (const r of trimmedRules) {
      if (r.length > MAX_RULE_LEN) return { ok: false, msg: `Each rule must be ${MAX_RULE_LEN} characters or less.` };
    }

    if (customThankYouText.length > MAX_THANKYOU_LEN) {
      return { ok: false, msg: `Thank-you page text must not exceed ${MAX_THANKYOU_LEN} characters.` };
    }

    if (tabName.trim().length > MAX_TAB_NAME_LEN) {
      return { ok: false, msg: `Tab name must be ${MAX_TAB_NAME_LEN} characters or less.` };
    }

    const maxNoOfQuestion = totalQuestions || FALLBACK_MAX_NO_OF_QUESTION;
    const noOfQuestionNum = parseInt(digitsOnly(noOfQuestion), 10);
    if (!Number.isInteger(noOfQuestionNum) || noOfQuestionNum < MIN_NO_OF_QUESTION) {
      return { ok: false, msg: `Number of questions to display must be at least ${MIN_NO_OF_QUESTION}.` };
    }
    if (noOfQuestionNum > maxNoOfQuestion) {
      return { ok: false, msg: `Number of questions to display can't exceed ${maxNoOfQuestion} (your question bank size).` };
    }

    return {
      ok: true,
      payload: {
        // Preserve fields owned by the Theme step / Questions "Settings" modal so Save doesn't wipe them out.
        option1: data?.option1, option2: data?.option2, option3: data?.option3,
        option4: data?.option4, option5: data?.option5, option6: data?.option6,
        cat_icon1: data?.cat_icon1, cat_icon2: data?.cat_icon2, cat_icon3: data?.cat_icon3,
        cat_icon4: data?.cat_icon4, cat_icon5: data?.cat_icon5, cat_icon6: data?.cat_icon6,

        rules: trimmedRules,
        tab_name: tabName.trim(),
        custom_text_thank_you_page: customThankYouText.trim(),
        no_of_question: noOfQuestionNum,
        shuffle,
        text_color: textColor,
        button_color: buttonColor,
        button_text_color: buttonTextColor,
        question_text_color: questionTextColor,
        option_box_color1: optionBoxColors[0],
        option_box_color2: optionBoxColors[1],
        option_box_color3: optionBoxColors[2],
        option_box_color4: optionBoxColors[3],
        option_box_color5: optionBoxColors[4],
        option_box_color6: optionBoxColors[5],
        answer_color: answerColor,
      },
    };
  };

  const handleSave = async () => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Data Updated", "", "success");
      navigate("/admin/questions");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  if ((status === "loading" && !data) || loading) {
    return (
      <div className="ls-admin-page">
        <div className="ls-admin-loading">Loading...</div>
      </div>
    );
  }

  if (!currentTheme) {
    return (
      <div className="ls-admin-page">
        <div className="back-button-holder">
          <button type="button" className="back-button" onClick={() => navigate("/admin/theme")}>
            <i className="fa-solid fa-arrow-left" /> Back
          </button>
        </div>
        <p className="ls-admin-muted">No theme selected yet — go back and create or select a theme first.</p>
      </div>
    );
  }

  return (
    <div className="ls-admin-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/theme")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <h2 className="ls-admin-title">Rules Settings</h2>

      <div className="ls-admin-row-2">
        {/* Left Card: Rules */}
        <div className="ls-admin-card">
          <h4 className="ls-admin-card-title">Tab Name</h4>
          <input
            type="text"
            className="ls-admin-input"
            placeholder="Tab Name"
            maxLength={MAX_TAB_NAME_LEN}
            value={tabName}
            onChange={(e) => setTabName(e.target.value)}
          />
          <p className="ls-admin-muted">
            * This will show as the browser tab name <br /> * character limit is {MAX_TAB_NAME_LEN}
          </p>

          <h4 className="ls-admin-card-title">Custom Text thank you page:</h4>
          <input
            type="text"
            className="ls-admin-input"
            maxLength={MAX_THANKYOU_LEN}
            value={customThankYouText}
            onChange={(e) => setCustomThankYouText(e.target.value)}
          />
          <p className="ls-admin-muted">
            * This text will reflect on the thank you page <br /> * character limit is {MAX_THANKYOU_LEN}
          </p>

          <h4 className="ls-admin-card-title">Add Custom Rules</h4>
          <div className="ls-admin-rules-repeater">
            {rules.map((rule, i) => (
              <div key={i} className="ls-admin-rule-row">
                <textarea
                  className="ls-admin-textarea"
                  placeholder="Rules"
                  maxLength={MAX_RULE_LEN}
                  value={rule}
                  onChange={(e) => handleRuleChange(i, e.target.value)}
                />
                <button type="button" className="ls-admin-btn-danger" onClick={() => handleRemoveRule(i)}>
                  <i className="fa-solid fa-times" />
                </button>
              </div>
            ))}
            <button type="button" className="ls-admin-btn-primary" onClick={handleAddRule}>
              <i className="fa-solid fa-plus" /> Add new rule
            </button>
            <p className="ls-admin-muted">
              * Keep {MAX_RULES} rules at the most <br />
              * At least 1 rule is required <br />
              * Character limit of each rule is {MAX_RULE_LEN}
            </p>
          </div>
        </div>

        {/* Right Card: Page Content */}
        <div className="ls-admin-card">
          <h4 className="ls-admin-card-title">Page Content</h4>

          <div className="ls-admin-inline-row">
            <label className="ls-admin-label">No of questions to display</label>
            <input
              type="text"
              inputMode="numeric"
              min={MIN_NO_OF_QUESTION}
              max={totalQuestions || FALLBACK_MAX_NO_OF_QUESTION}
              className="ls-admin-input ls-admin-input-sm"
              value={noOfQuestion}
              onChange={(e) => setNoOfQuestion(digitsOnly(e.target.value))}
              onBlur={() => {
                const max = totalQuestions || FALLBACK_MAX_NO_OF_QUESTION;
                setNoOfQuestion(String(clampInt(noOfQuestion, MIN_NO_OF_QUESTION, max, MIN_NO_OF_QUESTION)));
              }}
            />
          </div>
          <p className="code">
            {totalQuestions != null && `* You have ${totalQuestions} question${totalQuestions === 1 ? "" : "s"} in the question bank`}
            {totalQuestions != null && Number(noOfQuestion) > totalQuestions && (
              <>
                <br />* This is more than you have — only {totalQuestions} will show. Add more questions or lower this number.
              </>
            )}
            {totalQuestions != null && (
              <>
                <br />
                <button
                  type="button"
                  className="ls-admin-btn-secondary"
                  style={{ marginTop: 6 }}
                  onClick={() => setNoOfQuestion(String(totalQuestions))}
                >
                  Use all {totalQuestions}
                </button>
              </>
            )}
          </p>

          <div className="ls-admin-toggle-row">
            <label className="ls-admin-label">Shuffle Questions</label>
            <input type="checkbox" checked={shuffle} onChange={(e) => setShuffle(e.target.checked)} />
          </div>

          <div className="ls-admin-color-grid">
            <div className="ls-admin-color-item">
              <label>Text Color (question)</label>
              <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
            </div>
            <div className="ls-admin-color-item">
              <label>Button Color</label>
              <input type="color" value={buttonColor} onChange={(e) => setButtonColor(e.target.value)} />
            </div>
            <div className="ls-admin-color-item">
              <label>Button Text Color</label>
              <input type="color" value={buttonTextColor} onChange={(e) => setButtonTextColor(e.target.value)} />
            </div>
            <div className="ls-admin-color-item">
              <label>Question Text Color</label>
              <input type="color" value={questionTextColor} onChange={(e) => setQuestionTextColor(e.target.value)} />
            </div>
            <div className="ls-admin-color-item">
              <label>After selection Color</label>
              <input type="color" value={answerColor} onChange={(e) => setAnswerColor(e.target.value)} />
            </div>
          </div>

          <h4 className="ls-admin-card-title">Question box Color (per option)</h4>
          <div className="ls-admin-color-grid">
            {optionBoxColors.map((color, i) => (
              <div className="ls-admin-color-item" key={i}>
                <label>Option {i + 1}</label>
                <input
                  type="color"
                  value={color}
                  onChange={(e) => {
                    const arr = [...optionBoxColors];
                    arr[i] = e.target.value;
                    setOptionBoxColors(arr);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="ls-admin-action-holder">
        <button type="button" className="ls-admin-save-btn" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Next"} <i className="fa-solid fa-angle-double-right" />
        </button>
      </div>
    </div>
  );
}

export default Rules;
